export const AUTH_ERROR_CODES = Object.freeze({
  AUTHENTICATION_FAILED: 'AUTHENTICATION_FAILED',
  REFRESH_TOKEN_EXPIRED: 'REFRESH_TOKEN_EXPIRED',
  AUTH_MULTIPLE_LOGIN: 'AUTH_MULTIPLE_LOGIN',
})

/**
 * Custom API error class that extends the native Error class.
 * It includes additional information such as error code, status code, and an array of errors.
 */
export class ApiError extends Error {
  /**
   * Creates an instance of ApiError.
   *
   * @param {string} message - The error message.
   * @param {object} [options] - Additional options for the error.
   * @param {string} [options.errorCode] - Specific error code representing the error type.
   * @param {number|null} [options.statusCode] - HTTP status code associated with the error.
   * @param {Array} [options.errors] - An array containing detailed error information.
   */
  constructor(message, options = {}) {
    const {
      errorCode = 'BACKEND_REQUEST_FAILED',
      statusCode = null,
      errors = [],
    } = options

    super(message)
    this.name = 'ApiError'
    this.statusCode = statusCode
    this.errorCode = errorCode
    this.errors = errors
  }
}

/**
 * Processes an Axios error object and transforms it into a standardized ApiError instance.
 *
 * This function extracts relevant information from the provided Axios error object,
 * including the error message, HTTP status code, and an array of error details.
 * It maps the backend error structure to the ApiError format, ensuring consistent
 * error handling across the application.
 *
 * @param {object} error - The error object received from an Axios request.
 * @param {object} [error.response] - The HTTP response associated with the error.
 * @param {number} [error.response.status] - The HTTP status code of the response.
 * @param {object} [error.response.data] - The data payload of the error response.
 * @param {boolean} [error.response.data.success] - Indicates if the request was successful.
 * @param {string} [error.response.data.errorCode] - A code representing the type of error.
 * @param {string} [error.response.data.errorMessage] - A descriptive error message from the backend.
 * @param {Array|string} [error.response.data.errors] - An array or string of error details from the backend.
 * @param {Array|string} [error.response.data.error] - An additional error field from the backend.
 * @param {string} [error.message] - The default error message if not provided in response data.
 *
 * @returns {ApiError} An instance of ApiError containing the processed error information,
 *                     including the message, statusCode, and an array of error details.
 */
export function processError(error) {
  const _statusCode = error?.response?.status
  const _errorCode = error?.response?.data?.errorCode
  let _message = 'An unknown error occurred'
  let _errors = []

  if (error?.response?.data) {
    const { data } = error.response

    // Determine the main error message
    if (data.errorMessage) {
      _message = data.errorMessage
    }
    else if (data.message) {
      _message = data.message
    }
    else if (error.message) {
      _message = error.message
    }

    // Process the errors array or error object
    if (Array.isArray(data.errors)) {
      _errors = data.errors.map(err => ({ message: err }))
    }
    else if (data.errors) {
      _errors.push({ message: data.errors })
    }
    else if (data.error) {
      if (typeof data.error === 'string') {
        _errors.push({ message: data.error })
      }
      else if (Array.isArray(data.error)) {
        _errors = data.error.map(err => ({ message: err }))
      }
    }
  }
  else if (error.message) {
    _message = error.message
  }

  return new ApiError(_message, {
    statusCode: _statusCode,
    errorCode: _errorCode,
    errors: _errors,
  })
}

/**
 * Generates a fake Axios error object to represent a missing-token scenario.
 *
 * @returns {object} A fake Axios error object with a 401 status and standardized payload.
 */
export function createMissingTokenError() {
  return {
    response: {
      status: 401,
      data: {
        success: false,
        errorCode: AUTH_ERROR_CODES.AUTHENTICATION_FAILED,
        errorMessage: 'Request cancelled due to missing authentication token.',
        errors: [AUTH_ERROR_CODES.AUTHENTICATION_FAILED],
      },
    },
    message: 'Request cancelled because authentication token was not found.',
  }
}
